import React, { useContext, useEffect, useMemo, useState } from "react";
import { Typography, Box, Grid2 as Grid } from "@mui/material";
import { collection, onSnapshot, orderBy, query, limit } from "firebase/firestore";

import { Context } from "../Context";
import { db } from "../firebase";
import { TrainPlate } from "./TrainPlate";

type TrainItem = {
  lineId: string;          // e.g. "S1"
  routeId: string;         // e.g. "7459"
  active: boolean;
  lastUpdate: Date | null;
};

type RouteColumn = {
  route: string;      // lineId
  lines: TrainItem[]; // route docs
};

function toDateSafe(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const STALE_MS = 2 * 60 * 1000;
const LIVE_MS = 15 * 60 * 1000; // 15 minutes

function lineSortKey(id: string) {
  const t = id.trim().toUpperCase();
  const m = /^S(\d+)$/.exec(t);
  if (m) return parseInt(m[1], 10);
  return 9999;
}
function stableSecondaryKey(id: string) {
  return id.trim().toUpperCase();
}

const Sidebar: React.FC = () => {
  const { selected, setSelected } = useContext(Context);

  const [byLine, setByLine] = useState<Record<string, TrainItem[]>>({});
  const [lineIds, setLineIds] = useState<string[]>([]);

  // 1) Listen to lines (once)
  useEffect(() => {
    const qLines = query(collection(db, "lines"), orderBy("updatedAt", "desc"));

    const unsub = onSnapshot(
      qLines,
      (snap) => {
        const ids = snap.docs.map((d) => d.id);

        // keep only S-lines
        const filtered = ids.filter((id) => /^S\d+$/i.test(id.trim()));

        // stable ordering S1..S6..S60
        filtered.sort((a, b) => {
          const ka = lineSortKey(a);
          const kb = lineSortKey(b);
          if (ka !== kb) return ka - kb;
          return stableSecondaryKey(a).localeCompare(stableSecondaryKey(b));
        });

        setLineIds(filtered);
      },
      (err) => console.error("❌ lines onSnapshot error", err)
    );

    return () => unsub();
  }, []);

  // 2) For each line, listen to routes and populate byLine
  useEffect(() => {
    const unsubs: Array<() => void> = [];

    // wipe removed lines so UI doesn't show stale routes
    setByLine((prev) => {
      const next: Record<string, TrainItem[]> = {};
      lineIds.forEach((id) => (next[id] = prev[id] ?? []));
      return next;
    });

    lineIds.forEach((lineId) => {
      const qRoutes = query(
        collection(db, "lines", lineId, "routes"),
        orderBy("updatedAt", "desc"),
        limit(30)
      );

      const unsub = onSnapshot(
        qRoutes,
        (snap) => {
          const now = Date.now();

          const trains: TrainItem[] = snap.docs
            .map((d) => {
              const data: any = d.data();

              // Prefer unix_ms if present
              const updatedMs =
                typeof data?.updatedAt_unix_ms === "number"
                  ? data.updatedAt_unix_ms
                  : (() => {
                      const dt = toDateSafe(data?.updatedAt);
                      return dt ? dt.getTime() : null;
                    })();

              if (!updatedMs) return null;

              const isLive = now - updatedMs < LIVE_MS;

              return {
                lineId,
                routeId: d.id,
                active: isLive,
                lastUpdate: updatedMs ? new Date(updatedMs) : null,
                // optional: keep ms if you want
                // updatedMs,
              } as TrainItem;
            })
            .filter((x): x is TrainItem => Boolean(x))
            .filter((t) => t.active); // <-- ONLY LIVE routes shown

          setByLine((prev) => ({ ...prev, [lineId]: trains }));
        },
        (err) => console.error(`❌ routes onSnapshot error line=${lineId}`, err)
      );

      unsubs.push(unsub);
    });

    return () => unsubs.forEach((u) => u());
  }, [lineIds]);

  const routes: RouteColumn[] = useMemo(
    () =>
      lineIds.map((lineId) => ({
        route: lineId,
        lines: byLine[lineId] ?? [],
      })),
    [lineIds, byLine]
  );

  return (
    <React.Fragment>
      <Box sx={{ overflow: "auto", margin: "80px 4px 17px", color: "#98a1aa" }}>
        <Typography sx={{ fontSize: "12px", marginLeft: "25px" }} noWrap>
          LIVE TRAINS:
        </Typography>
      </Box>

      <Grid container spacing={0} sx={{ margin: 0 }}>
        {routes.map((routeCol) => (
          <Grid key={routeCol.route} size={4}>
            <Typography sx={{ fontSize: "12px", marginLeft: "15px", color: "#5d6679" }} noWrap>
              {routeCol.route}
            </Typography>

            {routeCol.lines.map((train) => (
              <TrainPlate
                key={`${train.lineId}:${train.routeId}`}
                train={train}
                selected={selected}
                setSelected={setSelected}
              />
            ))}
          </Grid>
        ))}
      </Grid>
    </React.Fragment>
  );
};

export default Sidebar;